import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { FaEnvelope, FaGithub, FaLinkedin, FaPhone } from "react-icons/fa";
import { Box, Container, Divider, Grid, IconButton, Link, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useLanguage } from "./context/LanguageContext";
import { brand } from "./brand";
import BrandLogo from "./components/BrandLogo";

const footerCopy = {
  no: {
    tagline: "Grunnlegger-ledet AI, styringsdata og operativ software for norske SMB-er.",
    navigation: "Navigasjon",
    contact: "Kontakt",
    connect: "Følg oss",
    response: "Svar innen én virkedag.",
    rights: "Alle rettigheter reservert.",
    org: "Org. nr.",
    links: [
      { label: "Tjenester", path: "/no/services" },
      { label: "Produkter", path: "/no/produkter" },
      { label: "Case", path: "/no/case" },
      { label: "Om", path: "/no/om" },
      { label: "Kontakt", path: "/no/kontakt" },
      { label: "Personvern", path: "/no/personvern" },
    ],
  },
  en: {
    tagline: "Founder-led AI workflows, management data, and operational software for Norwegian SMBs.",
    navigation: "Navigation",
    contact: "Contact",
    connect: "Connect",
    response: "Reply within one business day.",
    rights: "All rights reserved.",
    org: "Org. no.",
    links: [
      { label: "Services", path: "/en/services" },
      { label: "Products", path: "/en/products" },
      { label: "Case", path: "/en/case" },
      { label: "About", path: "/en/om" },
      { label: "Contact", path: "/en/kontakt" },
      { label: "Privacy", path: "/en/personvern" },
    ],
  },
};

const FooterHeading = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      color: "text.primary",
      fontWeight: 700,
      mb: 1.75,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      fontSize: "0.74rem",
    }}
  >
    {children}
  </Typography>
);

const Footer = () => {
  const { language } = useLanguage();
  const copy = footerCopy[language] ?? footerCopy.en;

  return (
    <Box
      component={motion.footer}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      sx={{
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <BrandLogo size="lg" sx={{ mb: 2 }} />
            <Typography color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 440, mb: 2.5 }}>
              {copy.tagline}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2.5}>
              <Typography variant="body2" color="text.secondary">
                {copy.org} {brand.orgNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {brand.location}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={6} md={2}>
            <FooterHeading>{copy.navigation}</FooterHeading>
            <Stack
              component="nav"
              aria-label={copy.navigation}
              spacing={{ xs: 0.25, md: 1 }}
            >
              {copy.links.map((item) => (
                <Link
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    width: "fit-content",
                    // Pad the row out to ~44px tall on touch devices for
                    // easier tapping; keeps tight rhythm on desktop.
                    py: { xs: 0.85, md: 0 },
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: { xs: 44, md: "auto" },
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FooterHeading>{copy.contact}</FooterHeading>
            <Stack spacing={1.25}>
              <Link href={`mailto:${brand.contact.email}`} sx={{ color: "text.secondary", textDecoration: "none" }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <FaEnvelope aria-hidden="true" />
                  <span>{brand.contact.email}</span>
                </Stack>
              </Link>
              <Link href={brand.contact.phoneHref} sx={{ color: "text.secondary", textDecoration: "none" }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <FaPhone aria-hidden="true" />
                  <span>{brand.contact.phoneDisplay}</span>
                </Stack>
              </Link>
              <Typography variant="body2" color="text.secondary">
                {copy.response}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={2}>
            <FooterHeading>{copy.connect}</FooterHeading>
            <Stack direction="row" spacing={1}>
              <IconButton
                component={Link}
                href={brand.social.linkedinCompany}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VImplement LinkedIn"
                sx={{
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { color: "primary.main", borderColor: "primary.main" },
                }}
              >
                <FaLinkedin aria-hidden="true" />
              </IconButton>
              <IconButton
                component={Link}
                href={brand.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VImplement GitHub"
                sx={{
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { color: "primary.main", borderColor: "primary.main" },
                }}
              >
                <FaGithub aria-hidden="true" />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body2" color="text.secondary" align="center">
          {"\u00A9"} {new Date().getFullYear()} {brand.name}. {copy.rights}
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
